// shared/src/oracle/source-registry.ts
// #95 (KEYSTONE) — the UNIFIED source registry. ONE read shape over every class the oracle knows,
// so the /oracle Sources tab can show ALL of them in one place instead of the current price-only
// grid: (1) oracle_sources of EVERY data_type (price AND macro — the fred macro source is today
// buried in the Macro header, invisible on Sources); (2) oracle_feeds_config (RSS/news feeds, today
// only visible on the Policy tab); (3) the data-access gateway's in-code KIND REGISTRY (registry.ts
// descriptors + freshness profiles — today surfaced nowhere). Foundation for #94 (news tier/weight),
// #96 (news policy), #98 (lineage), #76 (consumer provenance).
//
// PURE assembler (rows + descriptors in → validated bundle out), unit-testable in shared/ with no
// Worker/D1 dependency — the api-worker projector runs the SQL + reads the registry, then calls this.
// Config + provenance only (what exists, enabled, weight, tier, poll, staleness, pool, credential);
// per-source Health/Cost stays on the existing price bundle's rich view (dash-bundle.ts) — this is
// the "see every source" registry face, not a second health projection.

import { z } from 'zod';
import type { OracleSourceRow } from './oracle-store.js';
import type { OracleFeedConfigRow } from './feeds-config-store.js';
import type { AnyKindDescriptor } from '../dataplane/registry.js';
import { oracleSourceProvenance, explorerUrlForPoolRef } from './source-provenance.js';

// ─────────────────────────── view schemas ───────────────────────────

/** One oracle_sources row (ANY data_type) — config + #110 provenance. */
export const RegistrySourceViewSchema = z.object({
  source_id:       z.string(),
  data_type:       z.enum(['price', 'macro']),
  enabled:         z.boolean(),
  weight:          z.number(),
  tier:            z.enum(['A', 'B', 'C']),
  poll_interval_s: z.number(),
  staleness_max_s: z.number(),
  pool_ref:        z.string().nullable(),  // v3 pool address · v4 poolId · null
  pool_url:        z.string().nullable(),  // explorer link · null
  credential:      z.string().nullable(),  // Secrets-Store binding · null = public/keyless
  updated_at:      z.string(),
  updated_by:      z.string().nullable(),
});

/** One oracle_feeds_config row (RSS/news feed). No tier/weight/poll yet — that is exactly the gap
 *  #94/#96 fill; the registry surfaces the feed AS IT IS so the missing knobs are VISIBLE, not
 *  invented. */
export const RegistryFeedViewSchema = z.object({
  id:         z.number().int(),   // rowid alias — the PATCH/DELETE key
  feed_group: z.string(),
  url:        z.string(),
  max_items:  z.number(),
  enabled:    z.boolean(),
  note:       z.string().nullable(),
  updated_at: z.string(),
  updated_by: z.string().nullable(),
});

/** One registered data-access gateway KIND (registry.ts descriptor). Read-only projection — the
 *  gateway config IS the in-code registry + freshness constants, not an editable table. */
export const RegistryKindViewSchema = z.object({
  kind:                         z.string(),
  temporal:                     z.enum(['point', 'series', 'collection', 'document']),
  truth_clock:                  z.enum(['observed', 'created']),
  resolution:                   z.enum(['authoritative', 'consensus', 'first_healthy', 'corroborated', 'ranked', 'assembled']),
  backing:                      z.enum(['d1', 'kv', 'external_service']),
  cf_excluded:                  z.boolean(),
  default_freshness_max_age_ms: z.number(),
  // 'per-key' when the profile's basis is a per-key decider (e.g. crypto→observed, equity→created),
  // else the fixed basis. A decider cannot be rendered as one value without a key, so we label it.
  default_freshness_basis:      z.enum(['observed', 'created', 'per-key']),
});

export const OracleSourceRegistryBundleSchema = z.object({
  generated_at: z.string(),
  sources:      z.array(RegistrySourceViewSchema), // ALL oracle_sources (price + macro), by data_type then id
  feeds:        z.array(RegistryFeedViewSchema),   // oracle_feeds_config, by group then id
  kinds:        z.array(RegistryKindViewSchema),   // the gateway kind registry
});

export type RegistrySourceView          = z.infer<typeof RegistrySourceViewSchema>;
export type RegistryFeedView            = z.infer<typeof RegistryFeedViewSchema>;
export type RegistryKindView            = z.infer<typeof RegistryKindViewSchema>;
export type OracleSourceRegistryBundle  = z.infer<typeof OracleSourceRegistryBundleSchema>;

// ─────────────────────────── pure assembler ───────────────────────────

function sourceToView(s: OracleSourceRow): RegistrySourceView {
  const prov = oracleSourceProvenance(s.source_id);
  return {
    source_id:       s.source_id,
    data_type:       s.data_type,
    enabled:         s.enabled === 1,
    weight:          s.weight,
    tier:            s.tier,
    poll_interval_s: s.poll_interval_s,
    staleness_max_s: s.staleness_max_s,
    pool_ref:        prov?.poolRef?.value ?? null,
    pool_url:        explorerUrlForPoolRef(prov?.poolRef),
    credential:      prov?.credential ?? null,
    updated_at:      s.updated_at,
    updated_by:      s.updated_by,
  };
}

function feedToView(f: OracleFeedConfigRow): RegistryFeedView {
  return {
    id:         f.id,
    feed_group: f.feed_group,
    url:        f.url,
    max_items:  f.max_items,
    enabled:    f.enabled === 1,
    note:       f.note,
    updated_at: f.updated_at,
    updated_by: f.updated_by,
  };
}

function kindToView(d: AnyKindDescriptor): RegistryKindView {
  return {
    kind:                         d.kind,
    temporal:                     d.temporal,
    truth_clock:                  d.truthClock,
    resolution:                   d.resolution,
    backing:                      d.backing,
    cf_excluded:                  d.trust.cfExcluded,
    default_freshness_max_age_ms: d.defaultFreshness.maxAgeMs,
    default_freshness_basis:      typeof d.defaultFreshness.basis === 'function' ? 'per-key' : d.defaultFreshness.basis,
  };
}

/**
 * Assemble the unified source registry. PURE. The caller (api-worker) reads oracle_sources (all
 * data_types), oracle_feeds_config, and the gateway descriptors, then hands them here.
 */
export function assembleSourceRegistry(input: {
  readonly generatedAt: string;
  readonly sources:     readonly OracleSourceRow[];
  readonly feeds:       readonly OracleFeedConfigRow[];
  readonly kinds:       readonly AnyKindDescriptor[];
}): OracleSourceRegistryBundle {
  const sources = [...input.sources]
    .map(sourceToView)
    .sort((a, b) => (a.data_type === b.data_type ? a.source_id.localeCompare(b.source_id) : a.data_type.localeCompare(b.data_type)));
  const feeds = [...input.feeds]
    .map(feedToView)
    .sort((a, b) => (a.feed_group === b.feed_group ? a.id - b.id : a.feed_group.localeCompare(b.feed_group)));
  const kinds = [...input.kinds].map(kindToView).sort((a, b) => a.kind.localeCompare(b.kind));
  return { generated_at: input.generatedAt, sources, feeds, kinds };
}
